import React from 'react';
import { useLingui } from '@lingui/react';
import { IGraphData, IGraphDataGender } from '../../../../interfaces/types';
import { Trans } from '@lingui/macro';
import { useMemo, useState } from 'react';
import { getDateRangeTitle, numberWithSpaces } from '../../../../helpers/index';
import { EStatisticsInterval } from '../../../../../reducers/data';
import moment from 'moment';
import { store } from '../../../../../store';

interface IProps {
    graphResults?: (IGraphDataGender | IGraphData)[],
    filter?: string,
    interval?: EStatisticsInterval,
    isGender?: boolean
}

interface ICell {
    name: string,
    count: string,
    from: string,
    to: string
}

// Extended cell that holds M + F separately (used when isGender=true)
interface IGenderCell {
    name: string,
    mCount: string,
    fCount: string,
    from: string,
    to: string
}

type SortCol = 'label' | number   // number = column index (0-based into names array)
type SortDir = 'asc' | 'desc'

/** Strip a trailing '%' and parse to number safely */
function parseCount(c: string): number {
    if (!c || c === 'N/A') return 0
    return Number(c.replace('%', '').trim()) || 0
}

const TableGraph = ({graphResults, filter, interval, isGender}: IProps) => {
    const lingui = useLingui()

    const [sortCol, setSortCol] = useState<SortCol>('label')
    const [sortDir, setSortDir] = useState<SortDir>('asc')

    const handleSort = (col: SortCol) => {
        if (sortCol === col) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortCol(col)
            setSortDir(col === 'label' ? 'asc' : 'desc')
        }
    }

    const SortIcon = ({ col }: { col: SortCol }) => {
        if (sortCol !== col) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>
        return <span style={{ marginLeft: 4 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>
    }

    // Ordered unique group names
    const allNames: string[] = useMemo(() => {
        const names: string[] = []
        graphResults?.forEach(r => {
            if (!names.includes(r.name)) names.push(r.name)
        })
        return names
    }, [graphResults])

    // ── Gender table (isGender=true) ─────────────────────────────────────────
    // Build rows with separate M/F cells per group name.
    const genderRows: { start: string; end: string; cells: IGenderCell[] }[] = useMemo(() => {
        if (!isGender) return []
        const rows: { start: string; end: string; cells: IGenderCell[] }[] = []

        graphResults?.forEach(r => {
            const gData = r as IGraphDataGender
            gData.data.forEach(d => {
                let row = rows.find(rw => rw.start === d.start && rw.end === d.end)
                if (!row) {
                    row = { start: d.start, end: d.end, cells: [] }
                    rows.push(row)
                }
                let cell = row.cells.find(c => c.name === r.name)
                if (!cell) {
                    cell = { name: r.name, mCount: 'N/A', fCount: 'N/A', from: d.start, to: d.end }
                    row.cells.push(cell)
                }
                if (d.gender === 'M') cell.mCount = d.count
                if (d.gender === 'F') cell.fCount = d.count
            })
        })

        // Fill missing groups so columns are always aligned
        rows.forEach(row => {
            row.cells = allNames.map(name =>
                row.cells.find(c => c.name === name)
                ?? { name, mCount: 'N/A', fCount: 'N/A', from: row.start, to: row.end }
            )
        })
        return rows
    }, [graphResults, allNames, isGender])

    // ── Normal table (isGender=false) ────────────────────────────────────────
    const splitted: { start: string; end: string; values: ICell[] }[] = useMemo(() => {
        if (isGender) return []
        const splittedVal: { start: string; end: string; values: ICell[] }[] = []

        graphResults?.forEach(r => {
            r.data.forEach(d => {
                let was = false
                splittedVal.forEach(s => {
                    if (s.start === d.start && s.end === d.end) {
                        was = true
                        s.values.push({ count: d.count, name: r.name, from: d.start, to: d.end })
                    }
                })
                if (!was) {
                    splittedVal.push({
                        start: d.start,
                        end: d.end,
                        values: [{ count: d.count, name: r.name, from: d.start, to: d.end }]
                    })
                }
            })
        })

        // Fill missing cells so columns are always aligned
        splittedVal.forEach(s => {
            s.values = allNames.map(name =>
                s.values.find(v => v.name === name) ?? { count: 'N/A', name, from: s.start, to: s.end }
            )
        })
        return splittedVal
    }, [graphResults, allNames, isGender])

    // ── Sorted rows ──────────────────────────────────────────────────────────
    const sortedRows = useMemo(() => {
        if (isGender) {
            return [...genderRows].sort((a, b) => {
                if (sortCol === 'label') {
                    return sortDir === 'asc'
                        ? a.start.localeCompare(b.start)
                        : b.start.localeCompare(a.start)
                }
                // Sort by total (M+F) of the chosen column index
                const ci = sortCol as number
                const aV = parseCount(a.cells[ci]?.mCount) + parseCount(a.cells[ci]?.fCount)
                const bV = parseCount(b.cells[ci]?.mCount) + parseCount(b.cells[ci]?.fCount)
                return sortDir === 'asc' ? aV - bV : bV - aV
            })
        }
        return [...splitted].sort((a, b) => {
            let aVal: number | string
            let bVal: number | string
            if (sortCol === 'label') {
                aVal = a.start
                bVal = b.start
            } else {
                const aCell = a.values[sortCol as number]
                const bCell = b.values[sortCol as number]
                aVal = aCell?.count === 'N/A' ? -1 : parseCount(aCell?.count)
                bVal = bCell?.count === 'N/A' ? -1 : parseCount(bCell?.count)
            }
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1
            return 0
        })
    }, [splitted, genderRows, sortCol, sortDir, isGender])

    // ── Column sums ──────────────────────────────────────────────────────────
    // For gender mode: [{ m: number, f: number }] per column
    const genderSums = useMemo(() => {
        if (!isGender) return []
        return allNames.map((_, ci) =>
            genderRows.reduce((acc, row) => ({
                m: acc.m + parseCount(row.cells[ci]?.mCount),
                f: acc.f + parseCount(row.cells[ci]?.fCount),
            }), { m: 0, f: 0 })
        )
    }, [genderRows, allNames, isGender])

    const columnSums: number[] = useMemo(() => {
        if (isGender) return []
        return allNames.map((_, ci) =>
            splitted.reduce((acc, s) => acc + parseCount(s.values[ci]?.count), 0)
        )
    }, [splitted, allNames, isGender])

    const showSum = isGender ? genderRows.length > 1 : splitted.length > 1

    const urlExport = "https://api.clientportal.visitor.sk/table/?id=" + filter + "&token=" + store.getState().userStore.token

    return (
        <div className="table_scroll mgt-20 pdb-40">
            <div className="pdb-10">
                <a href={urlExport} className="btn_action text_nowrap_download text_w500">
                    <svg className="sico wh16"><use xlinkHref="/assets/layout/icons.svg#s_dl"></use></svg>
                    <span><Trans>Stiahnuť tabuľku</Trans></span>
                </a>
            </div>
            <table className={`table ${isGender ? '-gender' : ''}`}>
                <thead>
                    <tr>
                        <th
                            onClick={() => handleSort('label')}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                            <Trans>Dátum - časový interval</Trans>
                            <SortIcon col="label" />
                        </th>
                        {allNames.map((name, i) => (
                            <th
                                key={i}
                                colSpan={isGender ? 2 : 1}
                                onClick={() => handleSort(i)}
                                style={{ cursor: 'pointer', userSelect: 'none' }}
                            >
                                {name}
                                <SortIcon col={i} />
                            </th>
                        ))}
                    </tr>
                    {isGender && (
                        <tr className="thead">
                            <td></td>
                            {allNames.map((_, i) => (
                                <React.Fragment key={i}>
                                    <th><Trans>Muži</Trans></th>
                                    <th><Trans>Ženy</Trans></th>
                                </React.Fragment>
                            ))}
                        </tr>
                    )}
                </thead>
                <tbody>
                    {isGender
                        ? (sortedRows as typeof genderRows).map((row, i) => (
                            <tr key={i}>
                                <td>{getDateRangeTitle(row.start, row.end, interval!, lingui.i18n)}</td>
                                {row.cells.map((cell, j) => (
                                    <React.Fragment key={j}>
                                        <td>
                                            {cell.mCount === 'N/A'
                                                ? <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                                                : <div className="table_drop">
                                                    <span>{cell.mCount}</span>
                                                    <div className="drop">
                                                        <div><Trans>Názov</Trans>: <strong>{cell.name}</strong></div>
                                                        <div><Trans>Pohlavie</Trans>: <strong><Trans>Muži</Trans></strong></div>
                                                        <div><Trans>Dátum</Trans>: <strong>{getDateRangeTitle(cell.from, cell.to, interval!, lingui.i18n)}</strong></div>
                                                        <div><Trans>Stav</Trans>: <strong className="color_green">{cell.mCount}</strong></div>
                                                    </div>
                                                </div>
                                            }
                                        </td>
                                        <td>
                                            {cell.fCount === 'N/A'
                                                ? <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                                                : <div className="table_drop">
                                                    <span>{cell.fCount}</span>
                                                    <div className="drop">
                                                        <div><Trans>Názov</Trans>: <strong>{cell.name}</strong></div>
                                                        <div><Trans>Pohlavie</Trans>: <strong><Trans>Ženy</Trans></strong></div>
                                                        <div><Trans>Dátum</Trans>: <strong>{getDateRangeTitle(cell.from, cell.to, interval!, lingui.i18n)}</strong></div>
                                                        <div><Trans>Stav</Trans>: <strong className="color_green">{cell.fCount}</strong></div>
                                                    </div>
                                                </div>
                                            }
                                        </td>
                                    </React.Fragment>
                                ))}
                            </tr>
                        ))
                        : (sortedRows as typeof splitted).map((s, i) => (
                            <tr key={i}>
                                <td>{getDateRangeTitle(s.start, s.end, interval!, lingui.i18n)}</td>
                                {s.values.map((v, j) => (
                                    <td key={j}>
                                        {v.count === 'N/A' ? (
                                            <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                                        ) : (
                                            <div className="table_drop">
                                                <span>{v.count}</span>
                                                <div className="drop">
                                                    <div><Trans>Názov</Trans>: <strong>{v.name}</strong></div>
                                                    <div><Trans>Deň</Trans>: <strong>{moment(v.from).format('dddd')}</strong></div>
                                                    <div><Trans>Dátum</Trans>: <strong>{getDateRangeTitle(v.from, v.to, interval!, lingui.i18n)}</strong></div>
                                                    <div><Trans>Stav</Trans>: <strong className="color_green">{v.count}</strong></div>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))
                    }
                </tbody>
                {showSum && (
                    <tfoot>
                        <tr>
                            <td style={{ borderTop: '2px solid #A81A40', fontWeight: 700, fontSize: '13px' }}>
                                <Trans>Celkom</Trans> (Σ)
                            </td>
                            {isGender
                                ? genderSums.map((gs, i) => (
                                    <React.Fragment key={i}>
                                        <td style={{ borderTop: '2px solid #A81A40', textAlign: 'center' }}>
                                            <strong style={{ fontSize: '13px' }}>{gs.m > 0 ? numberWithSpaces(gs.m) : <span style={{ color: '#999', fontStyle: 'italic', fontWeight: 400 }}>N/A</span>}</strong>
                                        </td>
                                        <td style={{ borderTop: '2px solid #A81A40', textAlign: 'center' }}>
                                            <strong style={{ fontSize: '13px' }}>{gs.f > 0 ? numberWithSpaces(gs.f) : <span style={{ color: '#999', fontStyle: 'italic', fontWeight: 400 }}>N/A</span>}</strong>
                                        </td>
                                    </React.Fragment>
                                ))
                                : columnSums.map((sum, i) => (
                                    <td key={i} style={{ borderTop: '2px solid #A81A40', textAlign: 'center' }}>
                                        <strong style={{ fontSize: '13px' }}>
                                            {sum > 0 ? numberWithSpaces(sum) : (
                                                <span style={{ color: '#999', fontStyle: 'italic', fontWeight: 400 }}>N/A</span>
                                            )}
                                        </strong>
                                    </td>
                                ))
                            }
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    )
}

export default TableGraph
